import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Snackbar, Alert, Grid, Paper, useTheme, alpha, Fade
} from '@mui/material';
import UsageLimitCard from './common/UsageLimitCard';
import TopAgentsCard from './common/TopAgentsCard';
import { AnalyticsStatCard } from './common/AnalyticsStatCard';
import { MiniChatsChart, MiniMessagesChart } from './common/MiniAnalyticsCharts';
import RecentChatsCard from './common/RecentChatsCard';
import axios from 'axios';
import { getPlanLimit } from '../utils/planUtils';
import { useParams } from 'react-router-dom';
import Cookies from '../common/Cookies.js';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

export default function HomeAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUsage, setCurrentUsage] = useState(0);
  const [planType, setPlanType] = useState('B');
  const [topAgents, setTopAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [agentsError, setAgentsError] = useState(null);
  const [widgets, setWidgets] = useState({});
  const [chatsData, setChatsData] = useState([]);
  const [messagesData, setMessagesData] = useState([]);
  const params = useParams();
  const [open, setOpen] = React.useState(true);
  const theme = useTheme();

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      setLoading(true);
      setError(null);
      setAgentsLoading(true);
      setAgentsError(null);
      const tenantId = Cookies.getCookies('tenantId');
      if (!tenantId) {
        if (isMounted) {
          setError('Unable to load analytics: Tenant ID not found.');
          setLoading(false);
          setAgentsLoading(false);
        }
        return;
      }
      try {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const req = {
          tenantId,
          agentId: '',
          startDate: start.toISOString().slice(0, 10),
          endDate: end.toISOString().slice(0, 10)
        };
        const [overviewRes, planRes, widgetsRes] = await Promise.all([
          axios.post('/analytics/overview', req),
          axios.get('/account/plan'),
          axios.post('/analytics/widgets', req)
        ]);
        const chats = overviewRes.data.chats || [];
        const messages = overviewRes.data.messages || [];
        const totalChats = chats.reduce((sum, d) => sum + (d.count || 0), 0);
        let plan = planRes.data?.plan;
        if (!plan) plan = 'B';
        setPlanType(plan);
        const topAgentsData = widgetsRes.data.topAgents || [];
        if (isMounted) {
          setCurrentUsage(totalChats);
          setTopAgents(topAgentsData);
          setWidgets(widgetsRes.data || {});
          setChatsData(chats);
          setMessagesData(messages);
        }
      } catch (e) {
        if (isMounted) {
          setError('Failed to load usage data');
          setAgentsError('Failed to load top agents');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setAgentsLoading(false);
        }
      }
    }
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const fname = Cookies.getCookies('fname');

  return (
    <Fade in timeout={500}>
      <Box sx={{
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
      }}>
        {params.firstTime && (
          <Snackbar open={open} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert onClose={handleClose} severity="success" variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
              Welcome {Cookies.getCookies('fname')} {Cookies.getCookies('lname')}
            </Alert>
          </Snackbar>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5,
          }}>
            {greeting()}{fname ? `, ${fname}` : ''}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's your usage snapshot for this month
          </Typography>
        </Box>

        {/* Top Row: Usage + Top Agents */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6} sx={{ width: '48%' }}>
            <Paper elevation={0} sx={{
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}` },
            }}>
              <UsageLimitCard
                currentUsage={currentUsage}
                planType={planType}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} sx={{ width: '48%' }}>
            <TopAgentsCard
              agentData={topAgents}
              loading={agentsLoading}
              error={agentsError}
            />
          </Grid>
        </Grid>

        {/* Stats Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4} sx={{ width: '31%' }}>
            <AnalyticsStatCard
              label="Avg. Chat Duration"
              value={widgets?.avgChatDurationMin != null ? widgets.avgChatDurationMin.toFixed(1) : '--'}
              unit="min"
              loading={loading}
              error={error}
              icon={<TimerOutlinedIcon />}
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ width: '31%' }}>
            <AnalyticsStatCard
              label="Average Chat Rating"
              value={widgets?.avgChatRating != null ? widgets.avgChatRating.toFixed(2) : '--'}
              loading={loading}
              error={error}
              isRating
              icon={<StarOutlineRoundedIcon />}
              color={theme.palette.warning.main}
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ width: '31%' }}>
            <AnalyticsStatCard
              label="Avg. Messages / Chat"
              value={widgets?.avgMessagesPerChat != null ? widgets.avgMessagesPerChat.toFixed(1) : '--'}
              loading={loading}
              error={error}
              icon={<ChatBubbleOutlineRoundedIcon />}
              color={theme.palette.success.main}
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={3} sx={{ mb: 3, width: '100%' }}>
          <Grid item xs={12} md={6} sx={{ width: '48%' }}>
            <MiniChatsChart data={chatsData} loading={loading} error={error} />
          </Grid>
          <Grid item xs={12} md={6} sx={{ width: '48%' }}>
            <MiniMessagesChart data={messagesData} loading={loading} error={error} />
          </Grid>
        </Grid>

        {/* Recent Conversations */}
        <Box sx={{ mb: 3 }}>
          <RecentChatsCard />
        </Box>

        {/* CTA Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardRoundedIcon />}
            startIcon={<InsightsRoundedIcon />}
            onClick={() => window.location.assign('/#/analytics')}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
              },
            }}
          >
            Detailed Analytics
          </Button>
        </Box>
      </Box>
    </Fade>
  );
}
